import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Boat & RV Parking', path: '/boat-rv-parking-bells' },
    { name: 'Commercial Yard', path: '/commercial-yard-storage-bells' },
    { name: 'Size Guide', path: '/storage-sizes' },
    { name: 'FAQ', path: '/faq' },
    {
      name: 'Specials',
      children: [
        { name: 'People Who Serve', path: '/#people-who-serve' }
      ]
    },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const isDropdownActive = (children) =>
    children.some((child) => isActive(child.path));

  const toggleDropdown = (name) =>
    setActiveDropdown(activeDropdown === name ? null : name);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <div className="text-2xl font-bold">
                <span className="text-green-600">VD Storage</span>
                <span className="text-gray-800"> Bells</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              item.children ? (
                <div key={item.name} className="relative group">
                  <button className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isDropdownActive(item.children) ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'}`}>
                    {item.name}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                  <div className="absolute left-0 mt-0 w-52 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {item.children.map((child) => (
                      <Link key={child.name} to={child.path}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-green-600">
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
              <Link
                key={item.name}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive(item.path)
                  ? 'bg-green-50 text-green-600'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'
                  }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
                          )
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <a
              href="https://vdstoragebells.mysms360.com/login?utm_source=vdstoragebells.com&utm_content=nav_login"
              className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-700 border border-green-600 rounded-md hover:bg-green-50 transition-colors whitespace-nowrap"
              data-testid="login-button"
            >
              Make Payment/Login
            </a>
            <a
              href="https://vdstoragebells.mysms360.com/rent?utm_source=vdstoragebells.com&utm_content=nav_rent"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'navigation_rent_now',
                    'facility_name': 'VD Storage Bells'
                  });
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors shadow-md btn-primary whitespace-nowrap"
              data-testid="rent-storage-button"
            >
              Rent Now
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-green-600 hover:bg-gray-100 focus:outline-none"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              item.children ? (
                <div key={item.name}>
                  <button onClick={() => toggleDropdown(item.name)}
                    className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-base font-medium ${isDropdownActive(item.children) ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'}`}>
                    {item.name}
                    <ChevronDown className={`h-4 w-4 transform transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === item.name && (
                    <div className="pl-4 space-y-1 mt-1">
                      {item.children.map((child) => (
                        <Link key={child.name} to={child.path}
                          onClick={() => setIsOpen(false)}
                          className="block px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-green-600">
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(item.path)
                  ? 'bg-green-50 text-green-600'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'
                  }`}
              >
                {item.name}
              </Link>
                          )
            ))}
            <a
              href="https://vdstoragebells.mysms360.com/login?utm_source=vdstoragebells.com&utm_content=mobile_login"
              onClick={() => setIsOpen(false)}
              className="block w-full px-3 py-2 text-center text-sm font-medium text-green-600 border border-green-600 rounded-md hover:bg-green-50"
            >
              Make Payment/Login
            </a>
            <a
              href="https://vdstoragebells.mysms360.com/rent?utm_source=vdstoragebells.com&utm_content=mobile_rent"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'conversion_intent', {
                    'event_category': 'lead',
                    'event_label': 'mobile_menu_rent_now',
                    'facility_name': 'VD Storage Bells'
                  });
                }
              }}
              className="block w-full px-3 py-2 text-center text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              Rent Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;